import React, {
  FunctionComponent,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Noun, Verb } from '@trello/analytics';
import { Source, ActionSubjectId } from '@trello/atlassian-analytics';
import { N800 } from '@trello/colors';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { useAtlassianAccountMigrationQuery } from './AtlassianAccountMigrationQuery.generated';
import Banner from 'app/common/components/banner/banner';
import { forNamespace } from 'app/common/lib/util/i18n';
import {
  Overlay,
  OverlayAlignment,
  OverlayBackground,
} from 'app/src/components/Overlay';
import { useSnooze } from 'app/src/components/EmailHygieneWizard/useSnooze';
import {
  AtlassianAccountMigrationErrorCodes,
  getErrorCode,
} from './AtlassianAccountMigrationErrorCodes';
import { track, trackScreenEvent, SnowplowContext } from './Analytics';
import styles from './AtlassianAccountMigrationBanner.less';
import { AtlassianAccountMigrationAlertContainer } from './AtlassianAccountMigrationAlertContainer';
import { useLazyComponent } from '@trello/use-lazy-component';

let analyticsContext: SnowplowContext;

const format = forNamespace(['aa migration wizard', 'banner'], {
  shouldEscapeStrings: false,
});

export const forceMigrationQuery = /[?&]forceMigration=true/i;

export interface AtlassianAccountMigrationBannerProps {
  forceShow?: boolean;
}

export const AtlassianAccountMigrationBanner: FunctionComponent<AtlassianAccountMigrationBannerProps> = ({
  forceShow,
}) => {
  const AtlassianAccountMigrationError = useLazyComponent(() =>
    import(
      /* webpackChunkName: "aa-migration-error" */ './AtlassianAccountMigrationError'
    ),
  );
  const AtlassianAccountMigrationWizard = useLazyComponent(() =>
    import(
      /* webpackChunkName: "aa-migration-wizard" */ './AtlassianAccountMigrationWizard'
    ),
  );

  const [
    migrationErrorCode,
    setMigrationErrorCode,
  ] = useState<AtlassianAccountMigrationErrorCodes | null>(null);
  const [shouldShowBanner, setShouldShowBanner] = useState(false);
  const [isOverlayOpen, setOverlayOpen] = useState(false);

  const isTargeted = useFeatureFlag('aa4a.show-email-hygiene', false);

  const snoozeSeconds = useFeatureFlag('aa4a.peh-snooze-time', 0);
  const { isSnoozed, snooze } = useSnooze('peh-snooze', snoozeSeconds);

  const dataHook = useAtlassianAccountMigrationQuery({
    variables: { memberId: 'me' },
  });

  const me = dataHook.data && dataHook.data.member;

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const onboardingValue = searchParams.get('onboarding');
    const errorCode = onboardingValue && getErrorCode(onboardingValue);
    if (errorCode) {
      setMigrationErrorCode(errorCode);
    }
  }, []);

  const onDismissError = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('onboarding');
    const searchString = searchParams.toString();
    window.history.replaceState(
      {},
      document.title,
      window.location.href.replace(
        window.location.search,
        searchString ? '?' + searchParams.toString() : '',
      ),
    );
    setMigrationErrorCode(null);
  }, []);

  useEffect(() => {
    // Ensures we're done fetching data before opening the banner.
    if (!me) {
      return;
    }
    if (isSnoozed) {
      setShouldShowBanner(false);
      setOverlayOpen(false);
    } else if (forceShow || isTargeted) {
      setShouldShowBanner(true);
    }
  }, [me, forceShow, isTargeted, isSnoozed]);

  const openMigrationDialog = useCallback(() => {
    dataHook.refetch();
    setOverlayOpen(true);
  }, [dataHook]);

  const onClickUpdateAccount = useCallback(() => {
    openMigrationDialog();
    track({
      verb: Verb.INITIATES,
      noun: Noun.AA_MIGRATION_WIZARD,
      method: 'by clicking banner link',
      context: analyticsContext,
      source: Source.atlassianAccountMigrationBanner,
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: ActionSubjectId.atlassianAccountMigrationBannerLink,
    });
  }, [openMigrationDialog]);

  const onDismiss = useCallback(() => {
    setOverlayOpen(false);
    track({
      verb: Verb.CLOSES,
      noun: Noun.AA_MIGRATION_WIZARD,
      method: 'by dismissing overlay',
      context: analyticsContext,
      source: Source.atlassianAccountMigrationBanner,
      action: 'closed',
      actionSubject: 'overlay',
      actionSubjectId: ActionSubjectId.atlassianAccountMigrationOverlay,
    });
  }, []);

  const onClickSnoozeLink = useCallback(() => {
    snooze();
    track({
      verb: Verb.DISMISSES,
      noun: Noun.AA_MIGRATION_WIZARD,
      method: 'by clicking remind me later link',
      context: analyticsContext,
      source: Source.atlassianAccountMigrationBanner,
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId:
        ActionSubjectId.atlassianAccountMigrationBannerDismissLink,
    });
  }, [snooze]);

  if (!analyticsContext && !!me) {
    analyticsContext = {
      organizationId: null,
      totalEmailAddresses: me.logins?.length,
      totalClaimableEmailAddresses: me.logins?.filter((l) => l.claimable)
        .length,
    };
  }

  useEffect(() => {
    if (shouldShowBanner) {
      trackScreenEvent(
        Source.atlassianAccountMigrationBanner,
        analyticsContext,
      );
    }
  }, [shouldShowBanner]);

  const errors = (
    <>
      <AtlassianAccountMigrationAlertContainer />
      {migrationErrorCode ? (
        <Overlay
          background={OverlayBackground.LIGHT}
          closeOnEscape={false}
          onClose={() => {}}
          alignment={OverlayAlignment.TOP}
        >
          <Suspense fallback={null}>
            <AtlassianAccountMigrationError
              code={migrationErrorCode}
              onRetry={openMigrationDialog}
              onDismiss={onDismissError}
            />
          </Suspense>
        </Overlay>
      ) : null}
    </>
  );

  const banner =
    shouldShowBanner && me?.fullName ? (
      <Banner className={styles.banner} bannerColor={N800}>
        <p className={styles.text}>
          <span role="img" aria-label="">
            👋
          </span>
          {format('main-message', { name: me.fullName })}
          <a
            href="#"
            className={styles.updateLink}
            onClick={(e) => {
              e.preventDefault();
              onClickUpdateAccount();
            }}
          >
            {format('link-text')}
          </a>
          {snoozeSeconds > 0 && (
            <>
              {/* this symbol is the little dot that separates the links */}
              <span className={styles.linkSeparator}>&#183;</span>
              <a
                href="#"
                className={styles.dismissLink}
                onClick={(e) => {
                  e.preventDefault();
                  onClickSnoozeLink();
                }}
              >
                {format('snooze-link-text')}
              </a>
            </>
          )}
        </p>
      </Banner>
    ) : null;

  return (
    <div>
      {banner}
      {errors}
      {me?.email && isOverlayOpen && (
        <Overlay
          background={OverlayBackground.LIGHT}
          closeOnEscape={false}
          onClose={() => {}}
          alignment={OverlayAlignment.TOP}
        >
          <Suspense fallback={null}>
            <AtlassianAccountMigrationWizard
              analyticsContext={analyticsContext}
              onDismiss={onDismiss}
              hasTwoFactor={!!me.prefs?.twoFactor?.enabled}
              email={me.email}
            />
          </Suspense>
        </Overlay>
      )}
    </div>
  );
};
